import React, { ChangeEvent, Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, Link, useOutletContext } from "react-router-dom";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";

import {
  ArrowBack,
  Download,
  Search,
  MoreHoriz as MoreHorizIcon,
  Visibility as VisibilityIcon,
  Close,
  BarChart as BarChartIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Chip,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Fab,
  Grid,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";

import { ReactComponent as DatabaseOutlined } from "../../../../assets/images/ic-database-outlined.svg";
import { ReactComponent as PhantomIcon } from "../../../../assets/images/phantom.svg";
import { ErrorComponent } from "../../../components/error/error.component";
import { GdsCardSkeleton } from "../../../components/loading/analysis-skeletons.component";
import TableFixedResponsiveComponent from "../../../components/table/table-fixed-responsive.component";
import { useExportProgress } from "../../../hooks/useExportProgress";
import { PaginatedResult } from "../../../models/default/paginated-result";
import { Analysis, AnalysisResultExtractType } from "../../../models/general/analysis";
import {
  EMPTY_MATERIAL_STATUS_SUMMARY,
  MaterialStatusSummary,
  MaterialStatusUiStatus,
} from "../../../models/general/material-status";
import {
  generateMaterialStatus,
  getMaterialStatusSummary,
} from "../../../services/material-status.service";
import { URLS } from "../../../services/app-urls";
import { BaseService } from "../../../services/base-service";
import { translate } from "../../../translate/translate";
import { ExportType } from "../../../types/export.types";
import {
  buildAnalysisFileName,
  downloadExportFile,
  validateExportResponse,
} from "../../../utils/download-utils";
import {
  validateExportableData,
  validateExportApiResponse,
} from "../../../utils/export-validation.utils";
import { useAnalysisProgress } from "../../../hooks/useAnalysisProgress";
import DialogResultForecast from "./dialog-result/dialog-result-forecast";
import DialogResultInTransit from "./dialog-result/dialog-result-in-transit";
import DialogResultProjectedInventory from "./dialog-result/dialog-result-projected-inventory";
import DialogResultStock from "./dialog-result/dialog-result-stock";
import FilterMenuComponent from "../../../../app/page/general/gds-calculation/components/filter-menu.component";
import MaterialStatusDialog from "./components/material-status-dialog.component";
import MaterialStatusAction from "./components/material-status-action.component";
import { getNextWeeksWeekday, formatter } from "../../../../app/utils/formatter-utils";

type ExportOption = "filtered" | "complete";

const exportOptions: ExportOption[] = ["filtered", "complete"];

const getUniqueSupplierNames = (suppliers: any[]): string[] => {
  if (!Array.isArray(suppliers) || suppliers.length === 0) return [];
  return [...new Set(suppliers.map((s: any) => s.name).filter(Boolean))];
};

const getUniqueManufacturerPNs = (suppliers: any[]): string[] => {
  if (!Array.isArray(suppliers) || suppliers.length === 0) return [];
  return [...new Set(suppliers.map((s: any) => s.supplier_part_number).filter(Boolean))];
};

const GdsCalculationComponent = () => {
  const service = new BaseService<Analysis>(URLS.ANALYSIS);
  const serviceAnalysisResult = new BaseService(URLS.ANALYSIS_RESULT);
  const serviceExportAnalysis = new BaseService<Blob>(URLS.ANALYSIS_RESULT);

  const navigate = useNavigate();
  const location = useLocation();
  const { action: analysisId } = useParams();
  const outletContext = useOutletContext<any>();
  const isInsideAnalysisLayout = Boolean(outletContext);
  const { start: startExport } = useExportProgress(
    ExportType.EXPORT_ANALYSIS,
    Number(analysisId) || 0
  );

  /** Determines which tab is currently active based on the URL path. */
  const activeTab = location.pathname.endsWith("/material-status")
    ? "material-status"
    : location.pathname.endsWith("/materiais-demandas")
    ? "materiais-demandas"
    : "gds-calculation";

  const splitExportButtonRef = useRef<HTMLDivElement>(null);

  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [isFullyCompleted, setIsFullyCompleted] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginatedResult<Analysis>>();
  const [dataSource, setDataSource] = useState<any>([]);
  const [dataSourceCustom, setDataSourceCustom] = useState<any>([]);

  const [filterTerm, setFilterTerm] = useState("");
  const [notFoundTerms, setNotFoundTerms] = useState<string[]>([]);
  const [showSplitExportButton, setShowSplitExportButton] = useState(false);
  const [selectedExportOption, setSelectedExportOption] = useState<ExportOption>("filtered");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState([
    "description",
    "supplier",
    "manufacturer_part_number",
    "final_products",
  ]);
  const [selectedBalance, setSelectedBalance] = useState([
    "forecast",
    "in_transit",
    "stock",
    "projected_inventory",
  ]);

  const [sortModel, setSortModel] = useState<{ column: string; direction: "asc" | "desc" }[]>([]);

  const [anchorElFilter, setAnchorElFilter] = useState<null | HTMLElement>(null);
  const openFilter = Boolean(anchorElFilter);

  const [openModalForecast, setOpenModalForecast] = useState<boolean>(false);
  const [openModalInTransit, setOpenModalInTransit] = useState<boolean>(false);
  const [openModalStock, setOpenModalStock] = useState<boolean>(false);
  const [openModalProjectedInventory, setOpenModalProjectedInventory] = useState<boolean>(false);

  const [titleDialog, setTitleDialog] = useState("");

  const [analysisResultIdsForecast, setAnalysisResultIdsForecast] = useState([]);
  const [analysisResultIdsInTransit, setAnalysisResultIdsInTransit] = useState([]);
  const [analysisResultIdsStock, setAnalysisResultIdsStock] = useState([]);
  const [analysisResultIdsProjectedInventory, setAnalysisResultIdsProjectedInventory] = useState(
    []
  );

  const [openSupplierModal, setOpenSupplierModal] = useState(false);
  const [openMaterialStatusModal, setOpenMaterialStatusModal] = useState(false);
  const [materialStatusSummary, setMaterialStatusSummary] = useState<MaterialStatusSummary>(
    EMPTY_MATERIAL_STATUS_SUMMARY
  );
  const [materialStatusError, setMaterialStatusError] = useState<string | null>(null);
  const [generatingMaterialStatus, setGeneratingMaterialStatus] = useState(false);
  const [msStatus, setMsStatus] = useState<MaterialStatusUiStatus>("idle");
  const isGeneratingMaterialStatusRef = useRef(false);
  const [modalType, setModalType] = useState<"supplier" | "manufpn" | null>(null);
  const [modalTableData, setModalTableData] = useState<any[]>([]);
  const [modalPartNumber, setModalPartNumber] = useState("");

  const handleClickFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElFilter(event.currentTarget);
  };
  const handleCloseFilter = () => {
    setAnchorElFilter(null);
  };

  const handleOpenGeneratedMaterialStatus = () => {
    navigate(`/general/analysis/${analysisId}/material-status`);
  };

  const handleOpenMaterialStatusModal = async () => {
    setMaterialStatusError(null);
    setMaterialStatusSummary(EMPTY_MATERIAL_STATUS_SUMMARY);
    setOpenMaterialStatusModal(true);

    if (!analysis?.id) return;

    try {
      const response = await getMaterialStatusSummary(analysis.id);
      setMaterialStatusSummary(response.data);
    } catch {
      toast.error(translate.t("material_status_summary_error"));
    }
  };

  const handleCloseMaterialStatusModal = () => {
    if (isGeneratingMaterialStatusRef.current) return;
    setOpenMaterialStatusModal(false);
  };

  const handleConfirmMaterialStatus = async () => {
    if (!analysis?.id || isGeneratingMaterialStatusRef.current) return;

    isGeneratingMaterialStatusRef.current = true;
    setMaterialStatusError(null);
    setGeneratingMaterialStatus(true);
    setMsStatus("generating");
    try {
      const response = await generateMaterialStatus(analysis.id);
      const generationUuid = response?.data?.generation_uuid;
      const materialStatusUrl = response?.data?.material_status_url || null;
      setAnalysis((prev) =>
        prev
          ? {
              ...prev,
              has_material_status: true,
              material_status_uuid: generationUuid,
              material_status_url: materialStatusUrl,
            }
          : prev
      );
      outletContext?.setAnalysis?.((prev: Analysis | null) => prev ? {
        ...prev,
        has_material_status: true,
        material_status_uuid: generationUuid,
        material_status_url: materialStatusUrl,
      } : prev);
      setMsStatus("success");
      toast.success(translate.t("material_status_generation_success"));
      setOpenMaterialStatusModal(false);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message = detail || translate.t("material_status_generation_error");
      setMaterialStatusError(message);
      setMsStatus("error");
      setOpenMaterialStatusModal(false);
      toast.error(message);
    } finally {
      isGeneratingMaterialStatusRef.current = false;
      setGeneratingMaterialStatus(false);
    }
  };

  const handleApplyFilters = (columns: string[], balance: string[]) => {
    setSelectedColumns(columns);
    setSelectedBalance(balance);
  };

  const handleClickSystemData = () => {
    const path = `/general/analysis/${analysis?.id}/gds-simulation`;
    navigate(path, { state: analysis });
  };

  const getAnalysisInfo = () => {
    if (!analysisId) return;

    service.clearParameters();
    service
      .getById(analysisId)
      .then(({ data }) => {
        setAnalysis(data);
        setIsFullyCompleted(data.is_fully_completed ?? false);
        setMsStatus(data.has_material_status ? "success" : "idle");
      })
      .catch(error => ErrorComponent(error));
  };

  const analysisProgress = useAnalysisProgress(analysis?.id ?? 0);

  useEffect(() => {
    if (analysisProgress?.is_fully_completed !== undefined) {
      const fullyCompleted = !!analysisProgress.is_fully_completed;

      if (fullyCompleted && analysis && !isFullyCompleted) {
        window.location.reload();
        return;
      }

      setIsFullyCompleted(fullyCompleted);
    }
  }, [analysisProgress?.is_fully_completed, analysis, isFullyCompleted]);

  const isExportDisabled = !isFullyCompleted;

  const filter = () => {
    if (filterCalled.current && initialRender.current) return;

    filterCalled.current = true;

    if (analysisId === undefined) return;

    serviceAnalysisResult.clearParameters();
    if (filterTerm) serviceAnalysisResult.addParameter("search", filterTerm);
    serviceAnalysisResult.addParameter("limit", rowsPerPage);
    serviceAnalysisResult.addParameter("offset", page * rowsPerPage);

    if (sortModel.length > 0) {
      const orderParam = sortModel
        .map(({ column, direction }) => (direction === "asc" ? column : `-${column}`))
        .join(",");
      serviceAnalysisResult.addParameter("ordering", orderParam);
    }

    setIsLoading(true);

    serviceAnalysisResult
      .getFromDetailRoutePaginate(Number(analysisId), "gds")
      .then((response: any) => {
        setPagination(response.data);
        setDataSource(response.data.results.objects ?? []);
        setDataSourceCustom(response.data?.results?.all_dates ?? []);
        const notFound = response.data?.results?.not_found_terms ?? [];
        setNotFoundTerms(notFound);
        if (notFound.length > 0) {
          toast.warning(`${translate.t("part_number_not_found")}: ${notFound.join(", ")}`);
        }
      })
      .catch((error: any) => {
        ErrorComponent(error);
      })
      .finally(() => setIsLoading(false));
  };

  const initialRender = useRef(true);
  const filterCalled = useRef(false);

  useEffect(() => {
    getAnalysisInfo();
    filter();
  }, []);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    filter();
  }, [page, rowsPerPage, filterTerm, sortModel]);

  useEffect(() => {
    initialRender.current = false;
    return () => {
      initialRender.current = true;
      filterCalled.current = false;
    };
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onRequestSort = (column: string) => {
    setSortModel(prevModel => {
      const existing = prevModel.find(s => s.column === column);

      if (!existing) {
        return [...prevModel, { column, direction: "asc" }];
      }

      if (existing.direction === "asc") {
        return [...prevModel.filter(s => s.column !== column), { column, direction: "desc" }];
      }

      return prevModel.filter(s => s.column !== column);
    });
  };

  const handleExportAnalysis = async () => {
    if (!analysis) {
      toast.error(translate.t("analysis_not_found"));
      return;
    }

    // Validate data before export
    const validation = validateExportableData(dataSource, ExportType.EXPORT_ANALYSIS);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    try {
      serviceExportAnalysis.clearParameters();

      const shouldAddFilter = selectedExportOption === "filtered";
      if (shouldAddFilter) {
        if (filterTerm) {
          serviceExportAnalysis.addParameter("search", filterTerm);
        }
      }

      if (sortModel.length > 0) {
        const orderParam = sortModel
          .map(({ column, direction }) => (direction === "asc" ? column : `-${column}`))
          .join(",");
        serviceExportAnalysis.addParameter("ordering", orderParam);
      }

      // Pass currently visible columns/balances so the export matches the on-screen selection.
      if (selectedColumns.length > 0) {
        serviceExportAnalysis.addParameter("columns", selectedColumns.join(","));
      }
      if (selectedBalance.length > 0) {
        serviceExportAnalysis.addParameter("balances", selectedBalance.join(","));
      }

      const exportStartUrl = `${URLS.ANALYSIS_RESULT}${analysis.id}/export/`;
      const startResponse = await serviceExportAnalysis.loadUrlTyped<{
        code?: string;
        error?: string;
        id?: number;
        task_id?: string;
        file_url?: string;
        status?: string;
      }>(exportStartUrl);

      // Validate API response for NO_DATA_AVAILABLE error
      const apiValidation = validateExportApiResponse(startResponse.data);
      if (!apiValidation.isValid) {
        toast.error(apiValidation.message || translate.t("invalid_api_response"));
        return;
      }

      const apiValidationExport = validateExportResponse(startResponse.data);
      if (!apiValidationExport.isValid) {
        toast.error(apiValidationExport.error || translate.t("invalid_api_response"));
        return;
      }

      const { task_id, file_url, status } = startResponse.data;
      const fileName = buildAnalysisFileName(analysis.id);

      if (!task_id && status === "COMPLETED" && file_url) {
        toast.success(translate.t("export_already_created_downloading"));

        try {
          await downloadExportFile(file_url, { fileName });
        } catch (downloadError) {
          toast.error(translate.t("error_downloading_file_try_again"));
        }
        return;
      }

      startExport();
      toast.info(translate.t("export_started_notification"));
    } catch (error) {
      console.error(translate.t("export_error"), error);
    }
  };

  const handleMenuItemClick = (
    _: React.MouseEvent<HTMLLIElement, MouseEvent>,
    option: ExportOption
  ) => {
    setSelectedExportOption(option);
    setShowSplitExportButton(false);
  };

  const handleFilterSubmit = (
    values: { filterInputValue: string },
    {
      setSubmitting,
      setFieldValue,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      setFieldValue: (field: string, value: any) => void;
    }
  ) => {
    const formattedTerm = values.filterInputValue
      .split(";")
      .map(t => t.trim())
      .filter(t => t)
      .join(";");

    setFieldValue("filterInputValue", formattedTerm);
    setFilterTerm(formattedTerm);
    setPage(0);
    setSubmitting(false);
  };

  const toggleExportOptionsMenu = () => {
    setShowSplitExportButton(prevState => !prevState);
  };

  const closeExportOptionsMenu = (event: Event) => {
    if (splitExportButtonRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setShowSplitExportButton(false);
  };

  const getExportButtonLabel = (option: ExportOption) => {
    return translate.t(
      option === "filtered" ? "export_filtered_analysis" : "export_complete_analysis"
    );
  };

  const handleOpenForecastDialog = (partNumber: string, ids: any) => {
    if (ids && ids.length > 0) {
      setOpenModalForecast(true);
      setTitleDialog(partNumber);
      setAnalysisResultIdsForecast(ids);
    }
  };

  const handleCloseForecastDialog = () => {
    setOpenModalForecast(false);
  };

  const handleOpenInTransitDialog = (partNumber: string, ids: any) => {
    if (ids && ids.length > 0) {
      setOpenModalInTransit(true);
      setTitleDialog(partNumber);
      setAnalysisResultIdsInTransit(ids);
    }
  };

  const handleCloseInTransitDialog = () => {
    setOpenModalInTransit(false);
  };

  const handleOpenStockDialog = (partNumber: string, ids: any) => {
    if (ids && ids.length > 0) {
      setOpenModalStock(true);
      setTitleDialog(partNumber);
      setAnalysisResultIdsStock(ids);
    }
  };

  const handleCloseStockDialog = () => {
    setOpenModalStock(false);
  };

  const handleOpenProjectedInventoryDialog = (partNumber: string, ids: any) => {
    if (ids && ids.length > 0) {
      setOpenModalProjectedInventory(true);
      setTitleDialog(partNumber);
      setAnalysisResultIdsProjectedInventory(ids);
    }
  };

  const handleCloseProjectedInventoryDialog = () => {
    setOpenModalProjectedInventory(false);
  };

  /** Each projected inventory row reopens the extract it came from, in the same cell. */
  const handleOpenExtractFromProjectedInventory = (extractType: AnalysisResultExtractType) => {
    const ids = analysisResultIdsProjectedInventory;
    setOpenModalProjectedInventory(false);

    if (extractType === AnalysisResultExtractType.FORECAST) {
      handleOpenForecastDialog(titleDialog, ids);
    } else if (extractType === AnalysisResultExtractType.STOCK) {
      handleOpenStockDialog(titleDialog, ids);
    } else if (extractType === AnalysisResultExtractType.IN_TRANSIT) {
      handleOpenInTransitDialog(titleDialog, ids);
    }
  };

  const handleOpenSupplierModal = (suppliers: any[], type: "supplier" | "manufpn", partNumber: string) => {
    setModalType(type);
    setModalPartNumber(partNumber);

    let formatted;

    if (type === "manufpn") {
      const seen = new Set<string>();
      formatted = suppliers
        .filter((s: any) => s.supplier_part_number)
        .filter((s: any) => {
          if (seen.has(s.supplier_part_number)) return false;
          seen.add(s.supplier_part_number);
          return true;
        })
        .map((s: any) => ({ name: s.name, pn: s.supplier_part_number }));
    } else {
      const seen = new Set<string>();
      formatted = suppliers
        .filter((s: any) => {
          const key = `${s.name}::${s.supplier_part_number}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((s: any) => ({ name: s.name, pn: s.supplier_part_number }));
    }

    setModalTableData(formatted);
    setOpenSupplierModal(true);
  };

  const handleCloseSupplierModal = () => {
    setOpenSupplierModal(false);
    setModalTableData([]);
    setModalType(null);
  };

  const renderPartNumberCell = (row: any) => {
    return (
      <Box sx={{ gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {row.part_number}
        </Typography>
        {row.phantom_related && (
          <Chip
            icon={<PhantomIcon />}
            label="Phantom"
            size="small"
            sx={{
              color: "white",
              backgroundColor: "#9810FA",
              height: "16px",
              width: "90px",
              alignContent: "center",
            }}
          />
        )}
        {row.is_substitute && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: "#7B1FA2", lineHeight: 1.3 }}
          >
            {`↳ ${translate.t("substitutes")} ${row.substitutes_part_number}`}
          </Typography>
        )}
        {row.is_manual_addition && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: "#047857", lineHeight: 1.3 }}
          >
            {`↳ ${translate.t("manually_added")}`}
          </Typography>
        )}
      </Box>
    );
  };

  const rowCustomStyleHandler = (row: any) => {
    if (row?.is_manual_addition) return { backgroundColor: "#ECFDF5" };
    if (row?.is_substitute) return { backgroundColor: "#F3EAFB" };
    return {};
  };

  const cellCustomStyleHandler = (_column: string, row: any) => {
    if (row?.is_manual_addition) return { backgroundColor: "#ECFDF5" };
    if (row?.is_substitute) return { backgroundColor: "#F3EAFB" };
    return {};
  };

  const renderCellHandler = (column: string, _index: number, row: any) => {
    if (column === "part_number") {
      return renderPartNumberCell(row);
    }

    if (column === "supplier") {
      const suppliers = row.suppliers;

      if (!Array.isArray(suppliers) || suppliers.length === 0) return "";

      const uniqueNames = getUniqueSupplierNames(suppliers);
      const limited = uniqueNames.slice(0, 3);
      const isClickable = uniqueNames.length > 3;

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            "&:hover .more-icon": { opacity: 0 },
            "&:hover .eye-icon": { opacity: 1 },
          }}
        >
          {limited.map((name: string, i: number) => (
            <span
              key={i}
              style={{ cursor: "pointer", marginBottom: "4px" }}
              onClick={() => handleOpenSupplierModal(suppliers, "supplier", row.part_number)}
            >
              {name ?? "-"}
            </span>
          ))}

          {isClickable && (
            <Box
              onClick={() => handleOpenSupplierModal(suppliers, "supplier", row.part_number)}
              sx={{
                fontWeight: 600,
                cursor: "pointer",
                marginTop: 1,
                display: "inline-block",
                position: "relative",
              }}
            >
              <MoreHorizIcon className="more-icon" sx={{ color: "#90A4AE" }} />
              <VisibilityIcon
                className="eye-icon"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0,
                  transition: "opacity 0.2s",
                  color: "#90A4AE",
                }}
              />
            </Box>
          )}
        </Box>
      );
    }

    if (column === "manufacturer_part_number") {
      const suppliers = row.suppliers;

      if (!Array.isArray(suppliers) || suppliers.length === 0) return "";

      const uniqueManufPNs = getUniqueManufacturerPNs(suppliers);
      const limited = uniqueManufPNs.slice(0, 3);
      const isClickable = uniqueManufPNs.length > 3;

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            "&:hover .more-icon": { opacity: 0 },
            "&:hover .eye-icon": { opacity: 1 },
          }}
        >
          {limited.map((pn: string, i: number) => (
            <span
              key={i}
              onClick={() => handleOpenSupplierModal(suppliers, "manufpn", row.part_number)}
              style={{
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "180px",
                color: "#45485F",
                marginBottom: "4px",
              }}
            >
              {pn ?? "-"}
            </span>
          ))}

          {isClickable && (
            <Box
              onClick={() => handleOpenSupplierModal(suppliers, "manufpn", row.part_number)}
              sx={{
                fontWeight: 600,
                cursor: "pointer",
                marginTop: 1,
                display: "inline-block",
                position: "relative",
              }}
            >
              <MoreHorizIcon className="more-icon" sx={{ color: "#90A4AE" }} />
              <VisibilityIcon
                className="eye-icon"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0,
                  transition: "opacity 0.2s",
                  color: "#90A4AE",
                }}
              />
            </Box>
          )}
        </Box>
      );
    }

    return row[column];
  };

  const isOriginalAnalysis = analysis?.parent_analysis_id === undefined;

  const getAnalysisTitle = () => {
    if (!analysis) return "";

    if (analysis.forecast_date) {
      const date = formatter.formatDate(analysis.forecast_date);
      const period =
        analysis.effective_period !== undefined && analysis.effective_period !== null
          ? analysis.effective_period
          : analysis.period || 0;
      const boms = analysis.boms ? analysis.boms.length : 0;
      return translate.t("analysis_description_template", {
        date,
        period,
        boms,
        interpolation: { escapeValue: false },
      });
    }

    return analysis.description || "";
  };

  const isCurrentWeek = (analysisDateString: string) => {
    if (analysisDateString) {
      const analysisDate = new Date(analysisDateString);
      const lastDate = getNextWeeksWeekday(analysisDate);
      return new Date() >= lastDate;
    }
  };

  return (
    <>
      <section>
        {!isInsideAnalysisLayout && (
          <>
            <div
              className="floating-title floating-title-item"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <IconButton onClick={() => navigate("/general/analysis")}>
                  <ArrowBack color="primary" fontSize="large" fontWeight="fontWeightBold" />
                </IconButton>
                <Typography variant={"h5"}>{getAnalysisTitle()}</Typography>
              </div>
            </div>

            <Tabs
              value={activeTab}
              aria-label="Analysis view tabs"
              sx={{ borderBottom: 1, borderColor: "divider", mb: 2, mt: "-16px" }}
            >
              <Tab
                icon={<BarChartIcon fontSize="small" />}
                iconPosition="start"
                label={translate.t("gds_result")}
                value="gds-calculation"
                component={Link}
                to={`/general/analysis/${analysisId}/gds-calculation`}
                id="tab-gds-calculation"
                aria-controls="tabpanel-gds-calculation"
              />

              <Tooltip
                title={!isFullyCompleted ? translate.t("wait_for_analysis_completion") : ""}
                disableHoverListener={isFullyCompleted}
                placement="bottom"
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: "14px",
                      padding: "8px 12px",
                      maxWidth: 280,
                    },
                  },
                }}
              >
                <span>
                  <Tab
                    icon={<DescriptionIcon fontSize="small" />}
                    iconPosition="start"
                    label={translate.t("materials_demands")}
                    value="materiais-demandas"
                    component={Link}
                    to={`/general/analysis/${analysisId}/materiais-demandas`}
                    disabled={!isFullyCompleted}
                    id="tab-materiais-demandas"
                    aria-controls="tabpanel-materiais-demandas"
                  />
                </span>
              </Tooltip>
            </Tabs>
          </>
        )}

        {isLoading ? (
          <GdsCardSkeleton partNumbers={rowsPerPage || 10} />
        ) : (
          <>
            <Paper variant="outlined">
              <Box p={2}>
            <Formik
              enableReinitialize={true}
              initialValues={{ filterInputValue: "" }}
              onSubmit={handleFilterSubmit}
            >
              {({ values, setFieldValue }) => (
                <Form autoComplete="off">
                  <Grid
                    alignItems="center"
                    container
                    spacing={2}
                    direction={"row"}
                    wrap="nowrap"
                    sx={{ minWidth: 0 }}
                  >
                    <Grid item xs sx={{ minWidth: 0 }}>
                      <Field
                        as={TextField}
                        name="filterInputValue"
                        label={[
                          translate.t("part_number") +
                            ", " +
                            translate.t("description") +
                            ", " +
                            translate.t("model"),
                        ]}
                        fullWidth
                        variant={"outlined"}
                        value={values.filterInputValue}
                        onChange={(event: ChangeEvent<{ value: unknown }>) => {
                          setFieldValue("filterInputValue", event.target.value);
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Fab size="small" type="submit" color="primary">
                        <Search />
                      </Fab>
                    </Grid>
                    <Grid item>
                      <Divider orientation="vertical" variant="fullWidth" sx={{ height: "40px" }} />
                    </Grid>
                    <Grid
                      item
                      data-testid="gds-toolbar-actions"
                      sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        flexShrink: 0,
                        justifyContent: "flex-end",
                        gap: "5px",
                      }}
                    >
                      {filterTerm ? (
                        <Fragment>
                          <Tooltip
                            title={
                              isExportDisabled
                                ? translate.t("export_disabled_persistence_pending")
                                : ""
                            }
                            arrow
                          >
                            <span>
                              <ButtonGroup
                                variant="outlined"
                                color="primary"
                                ref={splitExportButtonRef}
                                aria-label="split button for export options"
                              >
                                <Button
                                  startIcon={<Download />}
                                  onClick={handleExportAnalysis}
                                  disabled={isExportDisabled}
                                >
                                  {getExportButtonLabel(selectedExportOption)}
                                </Button>
                                <Button
                                  size="small"
                                  aria-controls={
                                    showSplitExportButton ? "split-button-menu" : undefined
                                  }
                                  aria-expanded={showSplitExportButton ? "true" : undefined}
                                  aria-label="select export option"
                                  aria-haspopup="menu"
                                  onClick={toggleExportOptionsMenu}
                                  disabled={isExportDisabled}
                                >
                                  <ArrowDropDownIcon />
                                </Button>
                              </ButtonGroup>
                            </span>
                          </Tooltip>
                          <Popper
                            sx={{
                              zIndex: 1300,
                            }}
                            open={showSplitExportButton}
                            anchorEl={splitExportButtonRef.current}
                            role={undefined}
                            transition
                          >
                            {({ TransitionProps, placement }) => (
                              <Grow
                                {...TransitionProps}
                                style={{
                                  transformOrigin:
                                    placement === "bottom" ? "center top" : "center bottom",
                                }}
                              >
                                <Paper>
                                  <ClickAwayListener onClickAway={closeExportOptionsMenu}>
                                    <MenuList id="split-button-menu" autoFocusItem>
                                      {exportOptions.map(option => (
                                        <MenuItem
                                          key={option}
                                          selected={option === selectedExportOption}
                                          onClick={event => handleMenuItemClick(event, option)}
                                        >
                                          {getExportButtonLabel(option)}
                                        </MenuItem>
                                      ))}
                                    </MenuList>
                                  </ClickAwayListener>
                                </Paper>
                              </Grow>
                            )}
                          </Popper>
                        </Fragment>
                      ) : (
                        <Tooltip
                          title={
                            isExportDisabled
                              ? translate.t("export_disabled_persistence_pending")
                              : ""
                          }
                          arrow
                        >
                          <span>
                            <Button
                              startIcon={<Download />}
                              variant="outlined"
                              color="primary"
                              onClick={handleExportAnalysis}
                              disabled={isExportDisabled}
                            >
                              {translate.t("export_analysis")}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      <Button
                        startIcon={<FilterListOutlinedIcon />}
                        variant="outlined"
                        color="primary"
                        onClick={handleClickFilter}
                      >
                        {translate.t("filter")}
                      </Button>
                      <MaterialStatusAction
                        status={msStatus}
                        disabled={!isFullyCompleted}
                        errorDetail={materialStatusError}
                        onGenerate={handleOpenMaterialStatusModal}
                        onOpen={handleOpenGeneratedMaterialStatus}
                        onRetry={handleOpenMaterialStatusModal}
                      />

                      {/* Simulation GDS (Analysis) button */}
                      {isOriginalAnalysis && !isCurrentWeek(analysis?.created_at) && (
                        <Button
                          startIcon={<DatabaseOutlined height={18} width={18} />}
                          variant="outlined"
                          color="primary"
                          onClick={handleClickSystemData}
                        >
                          {translate.t("simulate_gds")}
                        </Button>
                      )}

                      <FilterMenuComponent
                        anchorEl={anchorElFilter}
                        open={openFilter}
                        onClose={handleCloseFilter}
                        onApplyFilters={handleApplyFilters}
                        selectedColumns={selectedColumns}
                        selectedBalance={selectedBalance}
                      />
                    </Grid>
                  </Grid>
                  {filterTerm && filterTerm.includes(";") && (
                    <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {filterTerm
                        .split(";")
                        .map(t => t.trim())
                        .filter(t => t)
                        .map((term, index) => {
                          const isNotFound = notFoundTerms.includes(term);
                          return (
                            <Chip
                              key={index}
                              label={term}
                              color={isNotFound ? "error" : "primary"}
                              variant={isNotFound ? "filled" : "outlined"}
                            />
                          );
                        })}
                    </Box>
                  )}
                </Form>
              )}
            </Formik>
          </Box>
        </Paper>
        <Card
          variant="outlined"
          sx={{
            marginTop: "16px",
            borderRadius: "4px",
            boxShadow:
              "0 2px 1px -1px rgba(0, 0, 0, .2), 0 1px 1px 0 rgba(0, 0, 0, .14), 0 1px 3px 0 rgba(0, 0, 0, .12)",
            padding: "16px",
          }}
        >
          <TableFixedResponsiveComponent
            state={dataSource}
            displayedFixedColumnsShow={["part_number", ...selectedColumns]}
            displayedFixedColumns={[
              "part_number",
              ...selectedColumns,
              ...(selectedBalance.length > 0 ? ["icons"] : []),
            ]}
            displayedCustomColumns={dataSourceCustom}
            displayedCustomColumnsShow={dataSourceCustom}
            displayedBalanceColumns={selectedBalance}
            count={pagination?.count}
            next={pagination?.next}
            previous={pagination?.previous}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            renderCellHandler={{
              part_number: renderCellHandler,
              supplier: renderCellHandler,
              manufacturer_part_number: renderCellHandler,
            }}
            rowCustomStyleHandler={rowCustomStyleHandler}
            cellCustomStyleHandler={cellCustomStyleHandler}
            sortingProps={{
              sortingColumns: ["part_number", "description", "final_products"],
              sortModel: sortModel,
              onRequestSort: onRequestSort,
            }}
            onClickValueForecast={handleOpenForecastDialog}
            onClickValueInTransit={handleOpenInTransitDialog}
            onClickValueStock={handleOpenStockDialog}
            onClickValueProjectedInventory={handleOpenProjectedInventoryDialog}
          />
        </Card>
      </>
    )}
  </section>
      <DialogResultForecast
        open={openModalForecast}
        onClose={handleCloseForecastDialog}
        title={titleDialog}
        analysisResultIds={analysisResultIdsForecast}
      />
      <DialogResultInTransit
        open={openModalInTransit}
        onClose={handleCloseInTransitDialog}
        title={titleDialog}
        analysisResultIds={analysisResultIdsInTransit}
      />
      <DialogResultStock
        open={openModalStock}
        onClose={handleCloseStockDialog}
        title={titleDialog}
        analysisResultIds={analysisResultIdsStock}
      />
      <DialogResultProjectedInventory
        open={openModalProjectedInventory}
        onClose={handleCloseProjectedInventoryDialog}
        title={titleDialog}
        analysisResultIds={analysisResultIdsProjectedInventory}
        onOpenExtract={handleOpenExtractFromProjectedInventory}
      />

      <MaterialStatusDialog
        open={openMaterialStatusModal}
        onClose={handleCloseMaterialStatusModal}
        summary={materialStatusSummary}
        error={materialStatusError}
        canConfirm={Boolean(
          materialStatusSummary.ready_to_generate &&
            !generatingMaterialStatus
        )}
        isGenerating={generatingMaterialStatus}
        onConfirm={handleConfirmMaterialStatus}
        analysisId={analysisId}
      />

      <Dialog open={openSupplierModal} onClose={handleCloseSupplierModal}>
        <Grid
          padding={2}
          container
          justifyContent="space-between"
          sx={{ backgroundColor: "primary.main" }}
        >
          <Grid item>
            <Typography sx={{ color: "#ffffff", fontWeight: "bold", fontSize: "16px" }}>
              {translate.t("part_number")} - {modalPartNumber}
            </Typography>
          </Grid>
          <Grid item sx={{ cursor: "pointer", color: "#ffffff" }}>
            <Close onClick={handleCloseSupplierModal} />
          </Grid>
        </Grid>
        <DialogContent sx={{ minWidth: "500px" }}>
          <Grid container flexDirection="column" gap={2} marginTop={1}>
            {modalTableData.length > 0 ? (
              <Box sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "10px",
                          borderBottom: "1px solid #e0e0e0",
                          fontWeight: "bold",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        {modalType === "supplier"
                          ? translate.t("supplier")
                          : translate.t("manufacturers")}
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "10px",
                          borderBottom: "1px solid #e0e0e0",
                          fontWeight: "bold",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        {modalType === "supplier"
                          ? translate.t("supplier_part_number_supplier")
                          : translate.t("supplier_part_number_manufacturer")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalTableData.map((item, index) => (
                      <tr key={index}>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #e0e0e0",
                            color: "#607883",
                          }}
                        >
                          {item.name || "-"}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #e0e0e0",
                            color: "#607883",
                          }}
                        >
                          {item.pn || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            ) : (
              <Typography sx={{ color: "#90a4ae" }}>{translate.t("no_data_available")}</Typography>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="flex-end" gap={1} padding={2}>
            <Grid item>
              <Button color="primary" variant="outlined" onClick={handleCloseSupplierModal}>
                {translate.t("close")}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GdsCalculationComponent;
